import { viewLabel, type ChromeLabelType } from '@featit/shared/chrome-label';
import type { Params } from 'react-router-dom';
import { legacyAddressTarget } from '@featit/shared/addressing/legacyAddress';
import {
  appPath,
  appRouteOf,
  parseAddress,
  workAreaReferenceFromParams,
  workAreaRoutePath,
} from '@featit/shared/addressing/paths';
import {
  workAreaQualifiedLabel,
  workAreaRouteRef,
} from '@featit/shared/addressing/workAreaRouteRef';
import { isGuestWorkArea } from '@featit/shared/guest-work-area';
import { type IProject } from '@featit/shared/types/index';
import { couldBeFeedbackId } from '@/features/feedback/feedbackId';
import { itemKeyOfRoute, sharedWithYouItemPath } from '@/features/sharing/sharedItemLink';
import { isWorkAreaBoundaryHandle } from './addressRoutes';
import { resolveRouteWorkArea, workAreasInNamespace } from './workAreaRoute';

/** The installation's own name, where an address belongs to no work area. */
const PRODUCT_NAME = 'FEATit';

/**
 * What an address shows before — or instead of — a page, decided once for every address the router
 * can land on, so the page and everything that names it cannot disagree (F-0250, I-0793).
 *
 * `WorkAreaRouteBoundary`, `NamespaceAddressPage` and `LegacyAddressRedirect` draw it; `Layout` names
 * it in the narrow header, the tab and the work-area switcher (`addressFrame`). ⛔ Nothing names a
 * not-found state from the address's shape or from the remembered selection: `/nosuchns` has a
 * namespace's shape, `/p/<unknown>/items` a work-items page's, and each was named by that shape, over
 * the last work area the reader visited.
 */
export type WorkAreaRouteStateType =
  /** An address with a new home, known from the address and the work areas: replaced by it. */
  | { kind: 'redirect'; to: string }
  /** A route nobody declared: on no work area, or on `workArea`, which exists. */
  | { kind: 'no-page'; workArea?: IProject }
  /** The work-area list has not answered, or its first load failed. */
  | { kind: 'waiting'; unknownRoute: boolean; reference?: string }
  /** A reference that names no work area the reader can open. */
  | { kind: 'no-work-area'; reference: string }
  /** A reference that names no work area, but a namespace the reader has work areas in. */
  | { kind: 'namespace'; namespace: string; workAreas: IProject[] }
  /** A reference that names several work areas; `route` is kept on whichever one is chosen. */
  | { kind: 'ambiguous'; reference: string; candidates: IProject[]; route: string }
  /** The address is a page: it renders. `routeRef` is the work area it is on, if any. */
  | { kind: 'page'; routeRef: string | undefined };

export function workAreaRouteState({
  pathname,
  matchedRef,
  boundary = true,
  projects,
  answered,
}: {
  pathname: string;
  /** The work area as the matched route's parameters spell it (`useWorkAreaRouteRef`). */
  matchedRef: string | undefined;
  /**
   * Whether the route that matched is one `WorkAreaRouteBoundary` answers — a work-area route or the
   * router's catch-all. An app address that reaches the boundary is one no route declared.
   */
  boundary?: boolean;
  projects: readonly IProject[];
  /** Whether the work-area list has answered, even empty. */
  answered: boolean;
}): WorkAreaRouteStateType {
  const address = parseAddress(pathname);
  if (address.kind === 'legacy') return legacyState(pathname, projects, answered);
  if (address.kind === 'namespace') {
    // A namespace has no routes of its own yet: `/acme/-/x` is nothing.
    if (address.route !== '') return { kind: 'no-page' };
    return namespaceState(address.namespace, projects, answered);
  }
  if (address.kind === 'app') {
    if (boundary) return { kind: 'no-page' };
    // I-0449: an address is not a feedback entry. `FeedbackDetailRoute` asks the same predicate.
    const feedback = /^\/feedback\/([^/]+)\/?$/.exec(appRouteOf(pathname) ?? '');
    if (feedback && !couldBeFeedbackId(decodeURIComponent(feedback[1]))) return { kind: 'no-page' };
    return { kind: 'page', routeRef: undefined };
  }
  // The root, a sign-in entry, an edge route: a page when declared, nothing when the catch-all took it.
  if (address.kind !== 'work-area') {
    return boundary ? { kind: 'no-page' } : { kind: 'page', routeRef: undefined };
  }

  // The work area as the address spells it, split at `-`; the matched parameters may run past it.
  const routeRef = address.reference;
  // A route this work area does not have: matched at the wrong depth, or by the catch-all, which
  // has no parameters at all.
  const unknownRoute = routeRef !== matchedRef;
  if (!answered) return { kind: 'waiting', unknownRoute, reference: routeRef };

  const resolution = resolveRouteWorkArea(projects, routeRef);
  if (resolution.outcome === 'not-found') {
    const inNamespace = workAreasInNamespace(projects, routeRef);
    return inNamespace.length > 0
      ? { kind: 'namespace', namespace: routeRef, workAreas: inNamespace }
      : { kind: 'no-work-area', reference: routeRef };
  }
  // I-1052 AC-2: a renamed or moved work area's old address redirects to the new one, keeping
  // whatever route followed it (`-/items`, a query, …) — the same "known from the address alone"
  // redirect `legacyState` already does for the pre-R-5 address shape, below.
  if (resolution.outcome === 'moved') {
    const project = projects.find((candidate) => candidate.id === resolution.workArea.id);
    const reference = project ? workAreaRouteRef(project) : '';
    if (project && reference.includes('/')) {
      return { kind: 'redirect', to: workAreaRoutePath(reference, address.route) };
    }
  }
  if (resolution.outcome === 'ambiguous') {
    return {
      kind: 'ambiguous',
      reference: routeRef,
      candidates: candidatesOf(projects, resolution.candidates),
      route: address.route,
    };
  }
  if (unknownRoute) {
    const workArea = projects.find((project) => project.id === resolution.workArea.id);
    // `/<work area>/-` and `/<work area>/-/` name no page after the separator: the work area's home
    // (the F-0774 dev walk drew "no page" there).
    if (workArea && address.route === '' && /\/-\/?$/.test(pathname)) {
      return { kind: 'redirect', to: workAreaRoutePath(workAreaRouteRef(workArea), '') };
    }
    return { kind: 'no-page', workArea };
  }
  // F-0621 (AGM-41): a guest reads a work area through the pages shared with them and nothing else,
  // so every member route (items, activity, settings …) takes them to the work area's home, which
  // opens the first of those pages. Never drawn: each would only say the guest may not read it.
  const workArea = projects.find((project) => project.id === resolution.workArea.id);
  if (isGuestWorkArea(workArea) && !isGuestRoute(address.route)) {
    // AGM-46: an item link opens the item as a guest reads it, in Shared with you, which says "No
    // item that you can read." alike for an item not shared with them and one that does not exist.
    const itemKey = itemKeyOfRoute(address.route);
    if (itemKey && workArea)
      return { kind: 'redirect', to: sharedWithYouItemPath(workArea.id, itemKey) };
    return { kind: 'redirect', to: workAreaRoutePath(routeRef) };
  }
  return { kind: 'page', routeRef };
}

/**
 * The routes of a work area a guest may open: its home and its pages (AGM-41), and a page opened for
 * one item (AGM-42, AGM-46), whose item the page's own route reads as the guest.
 */
export function isGuestRoute(route: string): boolean {
  return route === '' || route === '/' || /^\/pages\/[^/]+(?:\/items\/[^/]+)?\/?$/.test(route);
}

/**
 * `/acme`: the work areas in it, an old feedback link (`/<uuid>`, when no namespace answers), or
 * nothing. A real namespace always wins over the feedback shape.
 */
function namespaceState(
  namespace: string,
  projects: readonly IProject[],
  answered: boolean,
): WorkAreaRouteStateType {
  if (!answered) return { kind: 'waiting', unknownRoute: false, reference: namespace };
  const workAreas = workAreasInNamespace(projects, namespace);
  if (workAreas.length > 0) return { kind: 'namespace', namespace, workAreas };
  if (couldBeFeedbackId(namespace))
    return { kind: 'redirect', to: appPath(['feedback', namespace]) };
  return { kind: 'no-page' };
}

/**
 * An address from before R-5 (F-0250, AC-AD4 … AC-AD6). Its new address is known from the address
 * alone, except for a bare slug (`/p/tracker/items`), which only the reader's work areas resolve
 * (AC-M18): one answer → its address; several → the candidates; none → no work area.
 * ⛔ Never the first of several.
 */
function legacyState(
  pathname: string,
  projects: readonly IProject[],
  answered: boolean,
): WorkAreaRouteStateType {
  const target = legacyAddressTarget(pathname);
  if (!target) return { kind: 'no-page' };
  if (target.kind === 'address') return { kind: 'redirect', to: target.pathname };
  if (!answered) return { kind: 'waiting', unknownRoute: false, reference: target.slug };

  const resolution = resolveRouteWorkArea(projects, target.slug);
  // I-1052: an old `/p/<slug>` link can itself have been renamed since R-5's cutover; the hold
  // resolves it the same way the current-address branch below does.
  if (resolution.outcome === 'resolved' || resolution.outcome === 'moved') {
    const project = projects.find(({ id }) => id === resolution.workArea.id);
    const reference = project ? workAreaRouteRef(project) : '';
    /*
      ⛔ A work area the server sent without its namespace has no address but this one, and sending
      it here again would loop. No current server omits the namespace; a reader who meets one is
      told the address names nothing they can open, rather than watching the page spin.
    */
    if (project && reference.includes('/')) {
      return { kind: 'redirect', to: workAreaRoutePath(reference, target.route) };
    }
  }
  if (resolution.outcome === 'ambiguous') {
    return {
      kind: 'ambiguous',
      reference: target.slug,
      candidates: candidatesOf(projects, resolution.candidates),
      route: target.route,
    };
  }
  return { kind: 'no-work-area', reference: target.slug };
}

function candidatesOf(
  projects: readonly IProject[],
  candidates: readonly { id: string }[],
): IProject[] {
  return candidates
    .map((candidate) => projects.find((project) => project.id === candidate.id))
    .filter((project): project is IProject => Boolean(project));
}

/**
 * What the narrow header, the tab and the work-area switcher say over an address (I-0793): all
 * three from the state the screen draws, never from the address's shape or the last work area
 * visited.
 *
 * - `title`: the view's name, or `null` for a page, which its route names (`viewTitleForPath`).
 * - `scope`: the line above it and the tab's first half, or `null` to keep what the route and the
 *   selection give.
 * - `workArea`: the work area the switcher names — a work area, `null` for none, or `undefined` to
 *   keep the selection, which a page has already synced to its route.
 */
export interface IAddressFrame {
  /** The frame's name in English, as the frame logic reads it; `label` is what a screen draws. */
  title: string | null;
  /** I-2362: the frame's name as a chrome label, translated where it is drawn. */
  label: ChromeLabelType | null;
  scope: string | null;
  workArea: IProject | null | undefined;
}

const PAGE_FRAME: IAddressFrame = { title: null, label: null, scope: null, workArea: undefined };

/** The frames that say an address leads nowhere: no page at it, or no work area. */
const NO_PAGE_TITLE = 'No page here';
const NO_WORK_AREA_TITLE = 'No work area here';

/**
 * I-1265 (AC-18.13): whether the address on screen leads nowhere — the frames a person can land on
 * by a wrong link and must be able to leave by Back, not only by the page's forward link.
 */
export function isAddressErrorFrame(frame: Pick<IAddressFrame, 'title'>): boolean {
  return frame.title === NO_PAGE_TITLE || frame.title === NO_WORK_AREA_TITLE;
}

export function addressFrame(state: WorkAreaRouteStateType): IAddressFrame {
  switch (state.kind) {
    case 'no-page':
      // ⛔ The route cannot name it: `/acme/tracker/-/nope` reads as the work area's home by its
      // shape, and the header said "Home" over "There is no page at this address" (F-0250).
      return state.workArea
        ? {
            title: NO_PAGE_TITLE,
            label: viewLabel('noPageHere'),
            scope: workAreaQualifiedLabel(state.workArea),
            workArea: state.workArea,
          }
        : {
            title: NO_PAGE_TITLE,
            label: viewLabel('noPageHere'),
            scope: PRODUCT_NAME,
            workArea: null,
          };
    case 'waiting':
      // Whether the work area exists or not, an unknown route is none of its pages. A known one
      // keeps its route's name, so a page does not flash a second title while the list loads.
      return {
        title: state.unknownRoute ? NO_PAGE_TITLE : null,
        label: state.unknownRoute ? viewLabel('noPageHere') : null,
        scope: state.reference ?? null,
        workArea: undefined,
      };
    case 'no-work-area':
      return {
        title: NO_WORK_AREA_TITLE,
        label: viewLabel('noWorkAreaHere'),
        scope: state.reference,
        workArea: null,
      };
    case 'namespace':
      // F-0788 (PJ-10e): the bar names the namespace as its page does, never its key.
      return namespaceFrame(namespaceTitle(state.namespace, state.workAreas));
    case 'ambiguous':
      return {
        title: 'Choose a work area',
        label: viewLabel('chooseWorkArea'),
        scope: state.reference,
        workArea: null,
      };
    default:
      return PAGE_FRAME;
  }
}

/**
 * A namespace's own name, as its page heads it: the name its work areas carry, the address segment
 * only where none of them names it.
 */
export function namespaceTitle(namespace: string, workAreas: readonly IProject[]): string {
  const direct = workAreas.find(
    (project) => project.namespace === namespace || project.namespaceKey === namespace,
  );
  return direct?.namespaceName?.trim() || namespace;
}

function namespaceFrame(name: string): IAddressFrame {
  return { title: 'Work areas', label: viewLabel('workAreas'), scope: name, workArea: null };
}

/**
 * F-0788 (PJ-10e, `docs/concept-public-work-area-join.md`): a namespace's bare address whose page
 * names the namespace (`viewedPublicWorkArea`, id `namespace:<path>`) is that namespace's work areas,
 * by name. A stranger's address resolves to `no-page` before the Public list answers, and the bar,
 * the tab and Back read "No page here" over the namespace's own list.
 */
export function frameWithNamedNamespacePage(
  frame: IAddressFrame,
  pathname: string,
  viewed: { id: string; name: string } | null,
): IAddressFrame {
  if (!viewed?.id.startsWith('namespace:')) return frame;
  const address = parseAddress(pathname);
  if (address.kind !== 'namespace' || address.route !== '') return frame;
  return namespaceFrame(viewed.name);
}

/**
 * The frame for the address on screen, from the router's matches (`useMatches`), as `Layout` reads
 * it: whether the boundary answered this address is which route matched, and the rest is the
 * address.
 */
export function addressFrameAt({
  pathname,
  matches,
  projects,
  answered,
}: {
  pathname: string;
  matches: readonly { handle?: unknown; params: Readonly<Params<string>> }[];
  projects: readonly IProject[];
  answered: boolean;
}): IAddressFrame {
  const boundary = matches.find((match) => isWorkAreaBoundaryHandle(match.handle));
  return addressFrame(
    workAreaRouteState({
      pathname,
      matchedRef: boundary ? workAreaReferenceFromParams(boundary.params) : undefined,
      boundary: Boolean(boundary),
      projects,
      answered,
    }),
  );
}
